import { Fragment, useState } from 'react'

import { hero } from '@/content/site'

import { GraphCanvas } from './hero/GraphCanvas'
import { SceneTabs } from './hero/SceneTabs'
import { GRAPH_SCENE, LANDSCAPES_SCENE } from './hero/scenes'

/** Splits a headline into per-character spans for the hover wave. */
function SplitTitle({ text }: { text: string }) {
  let index = 0
  const words = text.split(' ')

  return (
    <span className="tl-hero-title">
      {words.map((word, wi) => (
        <Fragment key={`${word}-${wi}`}>
          {/* Real space between words — see shared/SplitTitle.tsx for why. */}
          {wi > 0 ? ' ' : null}
          <span className="tl-hero-title__word">
            {Array.from(word).map((ch, ci) => (
              <span
                key={`${ch}-${ci}`}
                className="tl-hero-title__char"
                style={{ ['--i' as string]: index++ }}
              >
                {ch}
              </span>
            ))}
          </span>
        </Fragment>
      ))}
    </span>
  )
}

const TABS = [
  { id: 'landscapes', label: hero.sceneTabs.landscapes },
  { id: 'graph', label: hero.sceneTabs.graph },
]

export function Hero() {
  const [activeTab, setActiveTab] = useState('landscapes')
  // Bumped on every tab click, including a re-click of the active tab, so the
  // scene's schedule restarts from step 0.
  const [resetKey, setResetKey] = useState(0)

  const selectTab = (id: string) => {
    setActiveTab(id)
    setResetKey((k) => k + 1)
  }

  const caption =
    activeTab === 'landscapes' ? hero.sceneCaptions.landscapes : hero.sceneCaptions.graph

  return (
    <section className="tl-hero relative overflow-hidden bg-bg pt-28 sm:pt-36 md:pt-44 pb-6 sm:pb-8 md:pb-12">
      {/* Ambient wash behind the headline */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 70% 50% at 30% 25%, rgb(47 191 143 / 0.07), transparent 60%)',
        }}
        aria-hidden="true"
      />

      <div className="shell relative">
        <p className="eyebrow">{hero.eyebrow}</p>

        <h1 className="max-w-4xl font-sans text-[1.75rem] sm:text-[2.4rem] md:text-[3.1rem] lg:text-[3.8rem] font-bold tracking-[-0.035em] leading-[1.04] text-ink">
          <SplitTitle text={hero.title} />
        </h1>

        <p className="mt-5 max-w-2xl text-sm md:text-base leading-relaxed text-ink-dim">
          {hero.subtitle}
        </p>

        <div className="mt-7 flex flex-wrap items-center gap-3">
          <a href={hero.primaryCta.href} className="cta cta-primary focus-ring">
            {hero.primaryCta.label}
          </a>
          <a href={hero.ghostCta.href} className="cta cta-ghost focus-ring">
            {hero.ghostCta.label}
          </a>
        </div>

        {/* Scene tabs + the live diagram. The hero *is* the product diagram. */}
        <div className="mt-10">
          <SceneTabs tabs={TABS} active={activeTab} onSelect={selectTab} />

          <div className="mt-4">
            {/* Both scenes stay mounted (hidden, not unmounted) so switching
                tabs does not flash. The inactive one's effect resets to -1. */}
            <div className={activeTab === 'landscapes' ? '' : 'hidden'}>
              <GraphCanvas
                scene={LANDSCAPES_SCENE}
                active={activeTab === 'landscapes'}
                resetKey={resetKey}
              />
            </div>
            <div className={activeTab === 'graph' ? '' : 'hidden'}>
              <GraphCanvas
                scene={GRAPH_SCENE}
                active={activeTab === 'graph'}
                resetKey={resetKey}
              />
            </div>
          </div>

          <p className="micro-label mt-4">{caption}</p>
        </div>
      </div>
    </section>
  )
}
